import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, adminAuth } from "@/lib/api";
import { formatApiError } from "@/lib/formatError";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  ArrowLeft, Upload as UploadIcon, X, Eye, Download, Loader2,
  FileText, Image as ImageIcon, Microscope, FlaskConical, Stethoscope, Save, Send,
} from "lucide-react";
import { toast } from "sonner";
import NutrientIndicatorsPanel from "@/components/nutrient/NutrientIndicatorsPanel";
import PractitionerReview from "@/components/practitioner/PractitionerReview";

const SEVERITY = ["none", "mild", "moderate", "severe"];
const LEVEL_LOW = ["low", "normal", "high"];

const BREAKAGE_OPTIONS = [
  "mid-shaft", "split-ends", "ends-only", "crown-area", "edges/hairline",
  "uniform thinning", "patchy breakage",
];
const CONTRIBUTING_FACTORS = [
  "stress", "sleep deficit", "hydration", "mineral imbalance", "thyroid pattern",
  "scalp inflammation", "post-partum", "perimenopause/menopause", "androgen-driven",
  "medication side effects", "traction/styling", "harsh products", "heat damage",
];

const UPLOAD_GROUPS = [
  {
    key: "zyto_report",
    title: "Zyto Reports",
    icon: FlaskConical,
    accept: "application/pdf,image/png,image/jpeg",
    accept_label: "PDF / PNG / JPG",
  },
  {
    key: "lab_result",
    title: "Lab Results",
    icon: FileText,
    accept: "application/pdf,image/png,image/jpeg",
    accept_label: "PDF / PNG / JPG",
  },
  {
    key: "microscope_image",
    title: "Microscopic Scalp Images",
    icon: Microscope,
    accept: "image/png,image/jpeg",
    accept_label: "PNG / JPG",
  },
];

const SCALP_PHOTO_CATEGORIES = [
  { key: "front", label: "Front / Headshot" },
  { key: "left_profile", label: "Left profile" },
  { key: "right_profile", label: "Right profile" },
  { key: "crown", label: "Crown / Top" },
  { key: "occipital", label: "Occipital / Nape" },
  { key: "closeup", label: "Close-up affected" },
];

const MAX_MB = 25;

// ---------- helpers ----------
async function fetchAuthBlob(id) {
  const res = await api.get(`/pro/uploads/${id}/file`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

// ---------- small UI building blocks ----------
const SectionCard = ({ title, subtitle, children, right, testId }) => (
  <section className="kcis-card p-6 sm:p-8 mb-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
      <div>
        <div className="kcis-overline text-purple-900">{subtitle}</div>
        <h2 className="font-display text-xl tracking-tight text-slate-900 mt-1">{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </section>
);

const ChipGroup = ({ values = [], options, onChange, testIdPrefix }) => {
  const toggle = (v) =>
    onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const on = values.includes(o);
        return (
          <button
            key={o}
            type="button"
            onClick={() => toggle(o)}
            data-testid={`${testIdPrefix}-${o.replace(/\s+/g, "_")}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${on
              ? "bg-purple-900 text-white border-purple-900"
              : "bg-white text-slate-700 border-slate-200 hover:border-purple-300"}`}
          >
            {o}
          </button>
        );
      })}
    </div>
  );
};

const SeveritySelect = ({ value, onChange, options = SEVERITY, testId }) => (
  <Select value={value || ""} onValueChange={(v) => onChange(v || null)}>
    <SelectTrigger className="rounded-xl bg-slate-50/60 border-slate-200" data-testid={testId}>
      <SelectValue placeholder="—" />
    </SelectTrigger>
    <SelectContent>
      {options.map((o) => (
        <SelectItem key={o} value={o}>{o}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

// ---------- upload card ----------
const UploadCard = ({ group, assessmentId, uploads, onUploaded, onDeleted }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const items = uploads.filter((u) => u.upload_type === group.key);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    const form = new FormData();
    form.append("upload_type", group.key);
    form.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/pro/assessments/${assessmentId}/uploads`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const Icon = group.icon;
  return (
    <div className="kcis-card p-5" data-testid={`upload-card-${group.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center">
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <div className="font-display text-base text-slate-900">{group.title}</div>
            <div className="text-xs text-slate-500">{group.accept_label} · ≤ {MAX_MB} MB</div>
          </div>
        </div>
        <Button
          variant="outline" size="sm"
          className="rounded-full border-slate-300"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`upload-btn-${group.key}`}
        >
          {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <UploadIcon className="h-4 w-4 mr-1" />}
          Upload
        </Button>
        <input ref={inputRef} type="file" accept={group.accept} className="hidden" onChange={onPick} />
      </div>
      <UploadList items={items} onDeleted={onDeleted} testIdPrefix={`upload-item-${group.key}`} />
    </div>
  );
};

const ScalpPhotoCard = ({ assessmentId, uploads, onUploaded, onDeleted }) => {
  const [busy, setBusy] = useState(null);
  const inputRefs = useRef({});

  const items = uploads.filter((u) => u.upload_type === "scalp_photo");

  const onPick = async (category, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    const form = new FormData();
    form.append("upload_type", "scalp_photo");
    form.append("scalp_photo_category", category);
    form.append("file", file);
    setBusy(category);
    try {
      const { data } = await api.post(`/pro/assessments/${assessmentId}/uploads`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data);
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err, "Upload failed"));
    } finally {
      setBusy(null);
      e.target.value = "";
    }
  };

  return (
    <div className="kcis-card p-5" data-testid="upload-card-scalp-set">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-9 w-9 rounded-xl bg-purple-50 text-purple-900 flex items-center justify-center">
          <Stethoscope className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-base text-slate-900">Clinical Scalp Photo Set</div>
          <div className="text-xs text-slate-500">PNG / JPG · ≤ {MAX_MB} MB · 6 angles</div>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {SCALP_PHOTO_CATEGORIES.map((cat) => {
          const captured = items.filter((u) => u.scalp_photo_category === cat.key);
          const isBusy = busy === cat.key;
          return (
            <div key={cat.key} className="border border-slate-200 rounded-xl p-3 bg-white">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">{cat.label}</div>
                <Button
                  size="sm" variant="ghost"
                  className="text-purple-900 hover:bg-purple-50 h-8 px-2"
                  onClick={() => inputRefs.current[cat.key]?.click()}
                  disabled={isBusy}
                  data-testid={`scalp-upload-btn-${cat.key}`}
                >
                  {isBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadIcon className="h-4 w-4" />}
                </Button>
                <input
                  ref={(el) => (inputRefs.current[cat.key] = el)}
                  type="file" accept="image/png,image/jpeg" className="hidden"
                  onChange={(e) => onPick(cat.key, e)}
                />
              </div>
              <UploadList
                items={captured}
                onDeleted={onDeleted}
                compact
                testIdPrefix={`scalp-item-${cat.key}`}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const UploadList = ({ items, onDeleted, compact = false, testIdPrefix }) => {
  if (!items.length) {
    return <div className="text-xs text-slate-400 italic mt-2">No files yet.</div>;
  }
  return (
    <div className={`mt-2 space-y-2 ${compact ? "" : ""}`}>
      {items.map((u) => (
        <UploadRow key={u.id} upload={u} onDeleted={onDeleted} compact={compact} testId={`${testIdPrefix}-${u.id}`} />
      ))}
    </div>
  );
};

const UploadRow = ({ upload, onDeleted, compact = false, testId }) => {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isImage = (upload.content_type || "").startsWith("image/");

  useEffect(() => {
    let active = true;
    if (isImage) {
      (async () => {
        try {
          const url = await fetchAuthBlob(upload.id);
          if (active) setPreviewUrl(url);
        } catch { /* ignore */ }
      })();
    }
    return () => {
      active = false;
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upload.id]);

  const openInline = async () => {
    setPreviewing(true);
    try {
      const url = await fetchAuthBlob(upload.id);
      const w = window.open();
      if (w) w.location.href = url;
      else toast.error("Pop-up blocked. Use Download instead.");
    } catch {
      toast.error("Preview failed");
    } finally {
      setPreviewing(false);
    }
  };

  const downloadFile = async () => {
    try {
      const res = await api.get(`/pro/uploads/${upload.id}/file?download=1`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = upload.filename; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${upload.filename}?`)) return;
    setDeleting(true);
    try {
      await api.delete(`/pro/uploads/${upload.id}`);
      onDeleted(upload.id);
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className={`flex items-center gap-3 p-2 rounded-lg border border-slate-200 bg-slate-50/40 ${compact ? "" : ""}`}
      data-testid={testId}
    >
      <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden shrink-0">
        {isImage && previewUrl ? (
          <img src={previewUrl} alt="" className="h-full w-full object-cover" />
        ) : isImage ? (
          <ImageIcon className="h-4 w-4 text-slate-400" />
        ) : (
          <FileText className="h-4 w-4 text-slate-400" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-800 truncate">{upload.filename}</div>
        <div className="text-[10px] text-slate-500">
          {(upload.size_bytes / 1024).toFixed(1)} KB · {upload.content_type}
        </div>
      </div>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={openInline} disabled={previewing} data-testid={`${testId}-view`}>
        {previewing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4 text-slate-600" />}
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={downloadFile} data-testid={`${testId}-download`}>
        <Download className="h-4 w-4 text-slate-600" />
      </Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 hover:bg-red-50" onClick={remove} disabled={deleting} data-testid={`${testId}-delete`}>
        {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4 text-red-600" />}
      </Button>
    </div>
  );
};

// ---------- main page ----------
export default function AdminProAssessment() {
  const navigate = useNavigate();
  const { clientId, assessmentId: routeAssessmentId } = useParams();
  const [client, setClient] = useState(null);
  const [proAssessment, setProAssessment] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [obs, setObs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingObs, setSavingObs] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Bootstrap: ensure admin token, load client, then resolve / create Pro assessment
  useEffect(() => {
    if (!adminAuth.getToken()) { navigate("/admin/login"); return; }
    (async () => {
      try {
        const { data: c } = await api.get(`/clients/${clientId}`);
        setClient(c);

        let pro;
        if (routeAssessmentId && routeAssessmentId !== "new") {
          const { data } = await api.get(`/pro/assessments/${routeAssessmentId}`);
          pro = data;
        } else {
          const { data: existing } = await api.get(`/clients/${clientId}/pro-assessments`);
          const open = existing.find((p) => p.status !== "report_generated");
          if (open) {
            pro = open;
          } else {
            const { data: created } = await api.post(`/pro/assessments`, { client_id: clientId });
            pro = created;
          }
        }
        setProAssessment(pro);
        setObs(pro.observations || {});

        const { data: ups } = await api.get(`/pro/assessments/${pro.id}/uploads`);
        setUploads(ups);
      } catch (err) {
        if (err?.response?.status === 401) {
          adminAuth.clear();
          navigate("/admin/login");
        } else {
          toast.error(formatApiError(err, "Could not load Pro assessment"));
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, routeAssessmentId]);

  const onUploaded = useCallback((u) => setUploads((prev) => [u, ...prev]), []);
  const onDeleted = useCallback((id) => setUploads((prev) => prev.filter((u) => u.id !== id)), []);

  const saveObservations = async () => {
    setSavingObs(true);
    try {
      const { data } = await api.patch(`/pro/assessments/${proAssessment.id}/observations`, {
        inflammation_severity: obs.inflammation_severity || null,
        erythema: obs.erythema || null,
        scaling: obs.scaling || null,
        follicular_activity: obs.follicular_activity || null,
        follicular_dropout: obs.follicular_dropout || null,
        miniaturization: obs.miniaturization || null,
        breakage_patterns: obs.breakage_patterns || [],
        traction_indicators: !!obs.traction_indicators,
        fibrosis_indicators: !!obs.fibrosis_indicators,
        oil_production: obs.oil_production || null,
        scalp_dryness: obs.scalp_dryness || null,
        suspected_contributing_factors: obs.suspected_contributing_factors || [],
        practitioner_notes: obs.practitioner_notes || "",
      });
      setProAssessment(data);
      setObs(data.observations);
      toast.success("Observations saved");
    } catch (err) {
      toast.error(formatApiError(err, "Could not save observations"));
    } finally {
      setSavingObs(false);
    }
  };

  const submitPro = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.patch(`/pro/assessments/${proAssessment.id}/status`, { status: "submitted" });
      setProAssessment(data);
      toast.success("Pro assessment submitted");
    } catch (err) {
      toast.error(formatApiError(err, "Submit failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadCount = uploads.length;
  const scalpSetCount = useMemo(
    () => new Set(uploads.filter((u) => u.upload_type === "scalp_photo").map((u) => u.scalp_photo_category)).size,
    [uploads]
  );

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <TopNav />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-purple-900" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!proAssessment || !client) return null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="admin-pro-assessment-page">
      <TopNav
        right={
          <Link to="/admin">
            <Button variant="ghost" className="rounded-full text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to dashboard
            </Button>
          </Link>
        }
      />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 w-full">
        <div className="kcis-fade-up mb-8">
          <div className="kcis-overline text-purple-900">KCIS Pro · Practitioner workflow</div>
          <h1 className="font-display text-3xl tracking-tight text-slate-900 mt-2">
            Pro assessment — <span className="text-purple-900">{client.name}</span>
          </h1>
          <p className="text-sm text-slate-600 mt-2">
            {client.email} · age {client.age} · status{" "}
            <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-900 text-xs border border-purple-100">
              {proAssessment.status}
            </span>
            {" · "}
            {uploadCount} upload{uploadCount === 1 ? "" : "s"} · scalp set {scalpSetCount}/6
          </p>
        </div>

        {/* KCIS Pro · Wellness Patterns (Sprint 2 — read-only) */}
        <NutrientIndicatorsPanel clientId={client.id} />

        {/* KCIS Pro · Practitioner Review (Sprint 6B4 — AI synthesis + adjacent overrides + audit) */}
        {adminAuth.isPro() ? <PractitionerReview clientId={client.id} /> : null}

        {/* Uploads */}
        <SectionCard
          title="Clinical evidence uploads"
          subtitle="Step 1 · Evidence"
          testId="pro-section-uploads"
        >
          <div className="grid md:grid-cols-3 gap-4 mb-4">
            {UPLOAD_GROUPS.map((g) => (
              <UploadCard
                key={g.key}
                group={g}
                assessmentId={proAssessment.id}
                uploads={uploads}
                onUploaded={onUploaded}
                onDeleted={onDeleted}
              />
            ))}
          </div>
          <ScalpPhotoCard
            assessmentId={proAssessment.id}
            uploads={uploads}
            onUploaded={onUploaded}
            onDeleted={onDeleted}
          />
        </SectionCard>

        {/* Observations */}
        <SectionCard
          title="Practitioner observations"
          subtitle="Step 2 · Structured clinical findings"
          testId="pro-section-observations"
          right={
            <Button
              onClick={saveObservations}
              disabled={savingObs}
              className="rounded-full bg-purple-900 hover:bg-purple-800 text-white"
              data-testid="pro-save-observations"
            >
              {savingObs ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save observations
            </Button>
          }
        >
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Field label="Inflammation severity">
              <SeveritySelect value={obs?.inflammation_severity}
                onChange={(v) => setObs({ ...obs, inflammation_severity: v })}
                testId="obs-inflammation_severity" />
            </Field>
            <Field label="Erythema / redness">
              <SeveritySelect value={obs?.erythema}
                onChange={(v) => setObs({ ...obs, erythema: v })}
                testId="obs-erythema" />
            </Field>
            <Field label="Scaling / flaking">
              <SeveritySelect value={obs?.scaling}
                onChange={(v) => setObs({ ...obs, scaling: v })}
                testId="obs-scaling" />
            </Field>
            <Field label="Follicular activity">
              <SeveritySelect value={obs?.follicular_activity} options={LEVEL_LOW}
                onChange={(v) => setObs({ ...obs, follicular_activity: v })}
                testId="obs-follicular_activity" />
            </Field>
            <Field label="Follicular dropout">
              <SeveritySelect value={obs?.follicular_dropout}
                onChange={(v) => setObs({ ...obs, follicular_dropout: v })}
                testId="obs-follicular_dropout" />
            </Field>
            <Field label="Miniaturization">
              <SeveritySelect value={obs?.miniaturization}
                onChange={(v) => setObs({ ...obs, miniaturization: v })}
                testId="obs-miniaturization" />
            </Field>
            <Field label="Oil production">
              <SeveritySelect value={obs?.oil_production} options={LEVEL_LOW}
                onChange={(v) => setObs({ ...obs, oil_production: v })}
                testId="obs-oil_production" />
            </Field>
            <Field label="Scalp dryness">
              <SeveritySelect value={obs?.scalp_dryness}
                onChange={(v) => setObs({ ...obs, scalp_dryness: v })}
                testId="obs-scalp_dryness" />
            </Field>
            <Field label="Traction indicators">
              <div className="flex items-center h-10">
                <Switch
                  checked={!!obs?.traction_indicators}
                  onCheckedChange={(v) => setObs({ ...obs, traction_indicators: v })}
                  data-testid="obs-traction_indicators"
                />
                <span className="ml-3 text-sm text-slate-600">
                  {obs?.traction_indicators ? "Observed" : "Not observed"}
                </span>
              </div>
            </Field>
            <Field label="Fibrosis / scarring indicators">
              <div className="flex items-center h-10">
                <Switch
                  checked={!!obs?.fibrosis_indicators}
                  onCheckedChange={(v) => setObs({ ...obs, fibrosis_indicators: v })}
                  data-testid="obs-fibrosis_indicators"
                />
                <span className="ml-3 text-sm text-slate-600">
                  {obs?.fibrosis_indicators ? "Observed" : "Not observed"}
                </span>
              </div>
            </Field>
          </div>

          <div className="mt-7">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Breakage patterns</Label>
            <div className="mt-2">
              <ChipGroup
                values={obs?.breakage_patterns || []}
                options={BREAKAGE_OPTIONS}
                onChange={(v) => setObs({ ...obs, breakage_patterns: v })}
                testIdPrefix="obs-breakage"
              />
            </div>
          </div>

          <div className="mt-6">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Suspected contributing factors</Label>
            <div className="mt-2">
              <ChipGroup
                values={obs?.suspected_contributing_factors || []}
                options={CONTRIBUTING_FACTORS}
                onChange={(v) => setObs({ ...obs, suspected_contributing_factors: v })}
                testIdPrefix="obs-contributing"
              />
            </div>
          </div>

          <div className="mt-6">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Practitioner notes (free text)</Label>
            <Textarea
              value={obs?.practitioner_notes || ""}
              onChange={(e) => setObs({ ...obs, practitioner_notes: e.target.value })}
              placeholder="Examination context, prior history, observations not captured above..."
              className="mt-2 min-h-[120px] rounded-xl border-slate-200 bg-slate-50/60"
              data-testid="obs-practitioner_notes"
            />
          </div>
        </SectionCard>

        {/* Footer actions */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-2">
          <div className="text-xs text-slate-500">
            Created {new Date(proAssessment.created_at).toLocaleString()} · last updated {new Date(proAssessment.updated_at).toLocaleString()}
          </div>
          <Button
            onClick={submitPro}
            disabled={submitting || proAssessment.status === "submitted"}
            variant="outline"
            className="rounded-full border-purple-300 text-purple-900 hover:bg-purple-50"
            data-testid="pro-submit-assessment"
          >
            {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            {proAssessment.status === "submitted" ? "Submitted" : "Mark as submitted"}
          </Button>
        </div>
      </main>
      <Footer />
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
